import { difference } from 'lodash'
import { currencies } from '../../shared'

export const colors = {
  pageviews: '#FF8656',
  clicks: '#FFB000',
  conversions: '#6A98FD',
  commission: '#0CCFAF',
}

export const dateDiffUnits = {
  hour: 'hours',
  date: 'days',
  week: 'weeks',
  month: 'months',
  year: 'years',
}

export const dateDiffFormats = {
  hour: 'YYYY-MM-DD HH',
  date: 'YYYY-MM-DD',
  week: 'YYYY-ww',
  month: 'YYYY-MM',
  year: 'YYYY',
}

export const graphHeight = 350

export const defaultGroupBy = 'name'
export const availableGroupBy = [
  'name', // advertiser
  'channel',
  'page',
  'device',
  'target',
  'network',
  'referer',
  'connection',
  'tracker',
  'shortcode',
  'status',
  'currency',
  'date',
  'week',
  'month',
  'year',

  'boost',
  'revshare',

  'advertiser',
]

export const groupLabels = {
  'date': 'Date',
  'name': 'Merchant',
  'tracker': 'Campaign',
  'channel': 'Site',
  'page': 'Page',
  'device': 'Device',
  'target': 'Target',
  'network': 'Network',
  'connection': 'Network account',
  'currency': 'Currency',
  'shortcode': 'Smart link',
  'referer': 'Referrer',

  'status': 'Status',
  'week': 'Week',
  'month': 'Month',
  'year': 'Year',

  'revshare': 'Boost (rev)',
  'boost': 'Boost',
}

export const filterLabels = {
  'date': 'Date',
  'month': 'Month',
  'year': 'Year',
  'statusKeys': 'Conversion status',
  'currencyCodes': 'Currency',
  'deviceTypes': 'Device',
  'advertiserUuids': 'Merchant',
  'channelUuids': 'Site',
  'creatorNotIds': 'Traffic',
  'networkUuids': 'Network',

  'pageUrlPaths': 'Page',
  'targetUrlPaths': 'Target',
  'referers': 'Referrer',
  'connectionUuids': 'Connection',
  'trackerUuids': 'Campaign',
  'urlShortCodes': 'Smart link',

  'names': 'Merchant',

  'subIds': 'Sub ID',
  'subId1s': 'Sub ID 1',
  'subId2s': 'Sub ID 2',
  'subId3s': 'Sub ID 3',
  'subId4s': 'Sub ID 4',
  'subId5s': 'Sub ID 5',

  'tags': 'Marketing tag',

  'revshareAccountUuids': 'Boost (revshare)',
  'assigneeUserUuids': 'Managers',
  'advertiserStatuses': 'Status',

  'boostDepartment': 'Boost dept.',

  'googleAdsCost': 'Ads cost',
  'googleAdsRoas': 'ROAS',
}

export const filterOptions = {
  'statusKeys': {
    'approved': 'Approved',
    'pending': 'Pending',
    'rejected': 'Rejected',
    'paid': 'Paid',
  },
  'currencyCodes': currencies.reduce((result, currency) => {
    result[currency] = currency
    return result
  }, {}),
  'deviceTypes': {
    'desktop': 'Desktop',
    'mobile': 'Mobile',
  },
  'advertiserStatuses': {
    '': 'No status',
    // 'stage:todo': 'To do',
    // 'stage:inprogress': 'In progress',
    // 'stage:notnow': 'Not now',
    // 'stage:no': 'No',
    // 'stage:complete': 'Completed',
    'partner:premium': 'Partner level: Premium',
    'partner:affiliate': 'Partner level: Affiliate',
    'partner:enterprise': 'Partner level: Enterprise',
    'deal:mrr': 'Deal type: MRR',
    'deal:enterprise': 'Deal type: Enterprise',
    'deal:advertisement': 'Deal type: Advertisement',
    'deal:direct': 'Deal type: Direct',
  },
}

export const groupFilterKeys = {
  'name': 'names',
  'channel': 'channelUuids',
  'page': 'pageUrlPaths',
  'device': 'deviceTypes',
  'target': 'targetUrlPaths',
  'network': 'networkUuids',
  'referer': 'referers',

  'connection': 'connectionUuids',
  'currency': 'currencyCodes',
  'status': 'statusKeys',
  'tracker': 'trackerUuids',
  'shortcode': 'urlShortCodes',

  'revshare': 'revshareAccountUuids',
  'boost': 'boostDepartment',
}

// v3: one column list per tab (v2 was one list for all tabs); the new key resets everyone to the defaults.
// The time tabs are one tab with a period switch, so they share a list.
export const tableColumnsKey = (group: string) =>
  `insights-statistics-columns-v3-${
    ['week', 'month', 'year'].includes(group) ? 'date' : group
  }`
export const allTableColumns = [
  'name',
  'pageviewCount',
  'clickCount',
  'ctr',
  'conversionCount',
  'cr',
  'rejectedCount',
  'rr',
  'conversionSale',
  'conversionCommission',
  'conversionClickCommission',
  'conversionTotalCommission',
  'conversionPendingCommission',
  'conversionRejectedCommission',
  'conversionBoostAddedValue',
  'rcr',
  'conversionRejectedSale',
  'grossSale',
  'grossConversionCount',
  'networks',
  'trackers',
  'assignees',
  'advertiserStatus',
  'ecr',
  'epc',
  'epm',
  'activeSinceDatetime',
  'googleAdsCost',
  'googleAdsRoas',
]

export const defaultTableColumns = difference(allTableColumns, [
  'conversionRejectedSale',
  'networks',
  'trackers',
  'googleAdsCost',
  'googleAdsRoas',
  'activeSinceDatetime',
])

export const availableTableColumns = {
  name: difference(allTableColumns, [
    'pageviewCount',
    'ctr',
    'epm',
    'conversionBoostAddedValue',
  ]),

  tracker: difference(allTableColumns, [
    'advertiserStatus',
    'assignees',
    'ctr',
    'epm',
    'conversionBoostAddedValue',
    'networks',
    'pageviewCount',
    'trackers',
    'googleAdsCost',
    'googleAdsRoas',
  ]),
  channel: difference(allTableColumns, [
    'assignees',
    'conversionBoostAddedValue',
    'advertiserStatus',
    'activeSinceDatetime',
    'googleAdsCost',
    'googleAdsRoas',
  ]),
  currency: difference(allTableColumns, [
    'pageviewCount',
    'ctr',
    'epm',
    'conversionBoostAddedValue',
    'assignees',
    'advertiserStatus',
    'activeSinceDatetime',
    'googleAdsCost',
    'googleAdsRoas',
  ]),
  target: difference(allTableColumns, [
    'advertiserStatus',
    'assignees',
    'ctr',
    'epm',
    'conversionBoostAddedValue',
    'networks',
    'pageviewCount',
    'activeSinceDatetime',
    'googleAdsCost',
    'googleAdsRoas',
  ]),
  network: difference(allTableColumns, [
    'advertiserStatus',
    'assignees',
    'ctr',
    'epm',
    'conversionBoostAddedValue',
    'networks',
    'pageviewCount',
    'activeSinceDatetime',
    'googleAdsCost',
    'googleAdsRoas',
  ]),
  shortcode: difference(allTableColumns, [
    'advertiserStatus',
    'assignees',
    'ctr',
    'epm',
    'conversionBoostAddedValue',
    'networks',
    'pageviewCount',
    'activeSinceDatetime',
    'googleAdsCost',
    'googleAdsRoas',
  ]),
  boost: difference(allTableColumns, ['pageviewCount', 'ctr', 'epm']),
  default: difference(allTableColumns, [
    'networks',
    'assignees',
    'advertiserStatus',
    'activeSinceDatetime',
    'googleAdsCost',
    'googleAdsRoas',
  ]),
}

export const omitCsvColumns = [
  'name',
  'networks',
  'trackers',
  'assignees',
  'advertiserStatus',
  'activeSinceDatetime',
  'googleAdsCost',
  'googleAdsRoas',
]

export const columnsDesc = [
  'pageviewCount',
  'clickCount',
  'ctr',
  'conversionCount',
  'cr',
  'rejectedCount',
  'rr',
  'conversionSale',
  'conversionCommission',
  'conversionClickCommission',
  'conversionTotalCommission',
  'conversionPendingCommission',
  'conversionRejectedCommission',
  'conversionBoostAddedValue',
  'rcr',
  'conversionRejectedSale',
  'grossSale',
  'grossConversionCount',
  'ecr',
  'epc',
  'epm',
  'activeSinceDatetime',
  'googleAdsCost',
  'googleAdsRoas',
]

export const tableLabels = {
  'name': '',
  'pageviewCount': 'Pageviews',
  'clickCount': 'Clicks',
  'ctr': 'CTR',
  'conversionCount': 'Conversions',
  'cr': 'CR',
  'rejectedCount': 'Rejections',
  'rr': 'Rejection rate',
  'rcr': 'Rejected commission rate',
  'conversionSale': 'Sale',
  'conversionCommission': 'Conv. commission',
  'conversionClickCommission': 'Click commission',
  'conversionTotalCommission': 'Total commission',
  'conversionPendingCommission': 'Pending commission',
  'conversionRejectedCommission': 'Rejected commission',
  'conversionRejectedSale': 'Rejected sale',
  'conversionBoostAddedValue': 'Boost added value',
  'grossSale': 'Gross sale',
  'grossConversionCount': 'Gross conversions',
  'ecr': 'ECR',
  'epc': 'EPC',
  'epm': 'EPM',
  'networks': 'Networks',
  'trackers': 'Campaigns',
  'assignees': 'Assigned',
  'advertiserStatus': 'Status',
  'advertiserStatusPartner': 'Partner level',
  'advertiserStatusDeal': 'Deal type',
  'activeSinceDatetime': 'Active since',
  'googleAdsCost': 'Ads cost',
  'googleAdsRoas': 'ROAS',
}

export const initialPage = 75
export const perPage = 50
